import { NgClass } from '@angular/common'
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { DividerModule } from 'primeng/divider'
import { PasswordModule } from 'primeng/password'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { ResetPasswordPayload } from 'app/_shared/interfaces/auth'
import { AuthService } from 'app/_shared/services/auth.service'
import { ToastService } from 'app/_shared/services/toast.service'
import {
  passwordMatch,
  passwordValidationLetters,
  passwordValidationNumber,
  passwordValidationSpecialCharacter
} from 'app/_shared/validators/password-match'
import { AppConfigService } from 'app/app-config.service'

type ErrorIconKey =
  'minLength' | 'number' | 'upperAndLower' | 'specialCharacter' | 'required' | 'notMatched'

@Component({
  selector: 'app-update-password',
  imports: [
    PasswordModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonComponent,
    DividerModule,
    NgClass,
    TranslatePipe
  ],
  templateUrl: './update-password.component.html',
  styleUrl: './update-password.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class UpdatePasswordComponent implements OnInit, OnDestroy {
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute)
  private toastService = inject(ToastService)
  private authService: AuthService = inject(AuthService)
  private router = inject(Router)
  private appConfigService: AppConfigService = inject(AppConfigService)
  private translateService = inject(TranslateService)
  fb = inject(FormBuilder)
  private destroy$: Subject<void> = new Subject<void>()

  private token: string
  private email: string
  protected form: FormGroup
  protected submitted: boolean = false
  protected isLoading: boolean = false

  errorIcons: Record<ErrorIconKey, string> = {
    minLength: 'error-icon',
    number: 'error-icon',
    upperAndLower: 'error-icon',
    specialCharacter: 'error-icon',
    required: 'error-icon',
    notMatched: 'error-icon'
  }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => {
      this.token = params['token']
      this.email = params['email']
    })
    this.buildForm()

    this.form.valueChanges.subscribe(() => {
      const errorMappings: { errorType: string; iconKey: ErrorIconKey; control?: string }[] = [
        { errorType: 'minlength', iconKey: 'minLength', control: 'password' },
        { errorType: 'atLeastOneNumber', iconKey: 'number', control: 'password' },
        { errorType: 'letterAndSymbol', iconKey: 'upperAndLower', control: 'password' },
        {
          errorType: 'atLeastOneSpecialCharacter',
          iconKey: 'specialCharacter',
          control: 'password'
        },
        { errorType: 'required', iconKey: 'required', control: 'password_confirmation' },
        { errorType: 'notMatched', iconKey: 'notMatched' }
      ]

      errorMappings.forEach(({ errorType, iconKey, control }) => {
        if (errorType === 'notMatched') {
          this.errorIcons[iconKey] = this.form.hasError('notMatched') ? 'error-icon' : 'check-icon'
        } else if (control) {
          const formControl = this.form.get(control)
          this.errorIcons[iconKey] = formControl?.hasError(errorType) ? 'error-icon' : 'check-icon'
        }
      })
    })
  }

  get password() {
    return this.form.get('password')
  }

  get confirmPassword() {
    return this.form.get('password_confirmation')
  }

  get storefrontId(): number | null {
    return this.appConfigService.getAppConfig.id
  }

  buildForm() {
    this.form = this.fb.group(
      {
        password: [
          null,
          [
            Validators.required,
            Validators.minLength(8),
            passwordValidationNumber(),
            passwordValidationLetters(),
            passwordValidationSpecialCharacter()
          ]
        ],
        password_confirmation: [null, [Validators.required]]
      },
      { validators: passwordMatch() }
    )
  }

  onSubmit() {
    this.submitted = true
    if (this.form.invalid) {
      this.form.markAllAsTouched()
      this.toastService.error(this.translateService.instant('ERRORS.fillFieldCorrectly'))
      return
    }
    this.resetPassword(this.prepareSubmitData())
  }

  resetPassword(formattedData: ResetPasswordPayload) {
    this.isLoading = true
    this.authService
      .resetPassword(formattedData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translateService.instant('VERIFICATION.passwordUpdatedSuccessfully')
          )
          this.router.navigate(['/login'])
          this.isLoading = false
        },
        error: err => {
          console.error(err)
          this.toastService.error(err.error.message)
        }
      })
  }

  prepareSubmitData() {
    const formData = this.form.value
    return {
      ...formData,
      email: this.email,
      token: this.token,
      storefront_id: this.storefrontId
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
